import React, { useState, useContext, useEffect, useMemo, useCallback } from 'react'
import Row from 'antd/lib/row'
import Col from 'antd/lib/col'
import Card from 'antd/lib/card'
import Flex from 'antd/lib/flex'
import message from 'antd/lib/message'
import cn from 'classnames'
import MaterialIcon from '../MaterialIcon'
import Button from '../../core/Button'
import * as api from '../../../services/api'
import { regionalPromoCode } from '../../../common/regional-offer'
import { PLANS, PRICE_TABS } from './constants'
import { recommendedPlanId } from './recommended-plan'
import { useTheme } from '../ThemeProvider/ThemeProvider'
import { logData } from '../../../services/firebase'
import { AuthContext } from '../../../context/firebase-context'
import { CTA_ID_BY_PLAN_ID } from '../../../services/paywall-analytics-cta'
import styles from './Pricing.module.scss'
import './ant-override.scss'

const PRICING_TEXT = {
  monthly: 'Billed monthly',
  '3month': 'Billed every 3 month',
  yearly: 'Billed annually'
}

const Tabs = ({ onSubscriptionSelect, subscriptionType }) => {
  const { themeMode } = useTheme() || {}
  return (
    <div className={cn(styles.head, styles[themeMode])}>
      <div className={styles.headWrapper}>
        {PRICE_TABS.map((tab, index) => {
          return (
            <div key={index} onClick={() => onSubscriptionSelect(tab.id)} className={styles.tabItem}>
              <div className={cn(styles.tabItemWrapper, { [styles.selected]: tab.id === subscriptionType })}>
                <div className={styles.tabName}>{tab.title} {tab.discount ? tab.discount.text : ''}</div>
              </div>
            </div>
          )
        })}
      </div>
    </div>
  )
}

/*
 * What a paid plan's button says.
 *
 * `create_session` turns any first checkout into a 7-day trial when the user
 * has not spent theirs, so this label is only telling the truth about what the
 * server is already doing -- the same thing the marketing pricing page says.
 *
 * Eligibility starts out false and is only ever upgraded, unlike brand, which
 * assumes the trial is available until told otherwise. A label that promises a
 * free week and then withdraws it a moment later is worse than one that starts
 * plain and improves.
 */
const paidPlanLabel = (trialEligible) => trialEligible ? 'Start 7-Day Free Trial' : 'Subscribe'

const PlanItemButton = ({
  plan,
  currentPlan,
  onPlanClick,
  subscriptionType,
  onUpgradePlan,
  trialEligible
}) => {
  if (currentPlan !== 'free') {
    return (
      <Button
        size='small'
        color='primary'
        mode='contained'
        onClick={() => onUpgradePlan({ priceType: subscriptionType, planName: plan.id })}
        rightIcon={<MaterialIcon name='add_circle' variant='label-default' style={{ fontSize: 20 }} />}
      >
        Manage Subscription
      </Button>
    )
  }

  return (
    <Button
      size='small'
      color='primary'
      mode='contained'
      onClick={() => onPlanClick(plan)}
      rightIcon={<MaterialIcon name='add_circle' variant='label-default' style={{ fontSize: 24 }} />}
    >
      {plan.id === 'free' ? 'Continue For Free' : paidPlanLabel(trialEligible)}
    </Button>
  )
}

const Pricing = ({ withFreeOption, onFreePlanClicked, onPlanCtaTracked, requiredPlan }) => {
  const { themeMode } = useTheme() || {}
  const user = useContext(AuthContext)
  // `requiredPlan` is the tier the blocked action needs -- showPaywall's first
  // argument, threaded down by UpgradePlanBody. See recommended-plan.js for
  // why the recommendation comes from that rather than from the trigger.
  const recommendedId = useMemo(() => recommendedPlanId(requiredPlan), [requiredPlan])
  const plans = useMemo(() => {
    const newPlans = [...PLANS]
    if (!withFreeOption) {
      newPlans.shift()
    }
    // The recommended tier leads, so the card a user has to buy to get past
    // the gate they just hit is the one they read first. Everything else keeps
    // its PLANS order.
    const index = newPlans.findIndex(plan => plan.id === recommendedId)
    if (index > 0) {
      newPlans.unshift(...newPlans.splice(index, 1))
    }
    return newPlans
  }, [withFreeOption, recommendedId])
  const [subscriptionType, setSubscriptionType] = useState('monthly')
  const [trialEligible, setTrialEligible] = useState(false)
  const currentPlan = useMemo(() => user.subscriptionPlan, [user])

  useEffect(() => {
    // Only free users reach checkout from here; everyone else is sent to the
    // billing portal, where a trial is not on offer.
    if (currentPlan !== 'free') return undefined
    let cancelled = false
    api.subscription.trialEligibility()
      .then(({ eligible }) => {
        if (!cancelled) {
          setTrialEligible(Boolean(eligible))
        }
      })
      // A failed lookup leaves the plain label up. The checkout still starts a
      // trial if the user is owed one -- the server decides that, not this.
      .catch(() => {})
    return () => {
      cancelled = true
    }
  }, [currentPlan])

  const onUpgradePlan = useCallback(async (data) => {
    const path = typeof window !== 'undefined' ? window.location.pathname : ''
    logData('studio_plan_cta_clicked', {
      plan_id: data?.planName || 'unknown',
      subscriptionType: data?.priceType,
      email: user.email,
      path,
      userUuid: user.userUuid
    })
    if (typeof onPlanCtaTracked === 'function') {
      onPlanCtaTracked(`manage_subscription_${data?.planName}`)
    }
    try {
      const response = await api.subscription.createPortalSession()
      window.open(response.portalUrl, '_blank')
    } catch (error) {
      message.error(error.toString())
    }
  }, [onPlanCtaTracked, user])

  const handlePlanClick = useCallback(async (plan) => {
    const path = typeof window !== 'undefined' ? window.location.pathname : ''
    logData('studio_plan_cta_clicked', {
      plan_id: plan.id || 'unknown',
      subscriptionType: subscriptionType,
      email: user.email,
      path,
      userUuid: user.userUuid
    })
    const ctaId = CTA_ID_BY_PLAN_ID[plan.id]
    if (ctaId && typeof onPlanCtaTracked === 'function') {
      onPlanCtaTracked(ctaId)
    }
    if (plan.id === 'free') {
      return onFreePlanClicked()
    }
    try {
      const response = await api.subscription.createSession({
        planId: plan.id,
        planName: plan.id,
        priceType: subscriptionType,
        // The marketing pricing page has always sent this; in-app checkout did
        // not, so an IN or AM user upgrading from inside Studio was quoted the
        // discount on the website and then charged full price here.
        promoCode: regionalPromoCode()
      })
      window.open(response.paymentUrl, '_blank')
    } catch (error) {
      message.error(error.toString())
    }
  }, [subscriptionType])

  return (
    <Flex className={cn(styles[themeMode], 'pricing-list-wrapper')} gap={24} vertical>
      <Tabs onSubscriptionSelect={setSubscriptionType} subscriptionType={subscriptionType} />
      <Row gutter={[8, 8]}>
        {
          plans.map((plan) => {
            return (
              <Col md={withFreeOption ? 8 : 12} sm={withFreeOption ? 8 : 12} lg={withFreeOption ? 8 : 12} xs={24} key={plan.id}>
                <div
                  // The pill is positioned against this wrapper rather than
                  // against the card, so it does not depend on which antd
                  // internals happen to be positioned. The plan's accent
                  // colour is set here too, as a custom property, so it
                  // reaches both the pill and the card's border.
                  className={cn('pricing-card-slot', {
                    [`pricing-card-recommended-${plan.id}`]: plan.id === recommendedId
                  })}
                >
                  {plan.id === recommendedId && (
                    <span className={styles.recommendedTag}>RECOMMENDED</span>
                  )}
                  <Card
                    // A global class rather than a module one: the border it
                    // overrides is set on `.pricing-list-wrapper .ant-card` in
                    // ant-override.scss, and a hashed single class would lose
                    // that specificity contest.
                    className={cn({ 'pricing-card-recommended': plan.id === recommendedId })}
                    title={(
                      <>
                        <div className={cn(styles.line, {
                          [styles.basic]: plan.id === 'basic',
                          [styles.basicPlus]: plan.id === 'basicPlus',
                          [styles.pro]: plan.id === 'pro'
                        })}
                        />
                        {plan.title}
                      </>
                    )}
                    actions={[
                      <PlanItemButton
                        key={plan.id}
                        plan={plan}
                        currentPlan={currentPlan}
                        onPlanClick={handlePlanClick}
                        onUpgradePlan={onUpgradePlan}
                        trialEligible={trialEligible}
                      />
                    ]}
                    variant='borderless'
                  >
                    <Flex vertical gap={8}>
                      <div className={styles.priceContainer}>
                        <div className={styles.description}>{plan.descriptions}</div>
                        {plan.id === currentPlan && <div className={styles.currentPlan}>Your Current Plan</div>}
                        <div className={styles.priceWrapper}>
                          <div className={cn(styles.price, {
                            [styles.basic]: plan.id === 'basic',
                            [styles.basicPlus]: plan.id === 'basicPlus',
                            [styles.pro]: plan.id === 'pro'
                          })}
                          >${plan.prices[subscriptionType].price}
                          </div>
                          {plan.id !== 'free' && <div className={styles.priceText}>{PRICING_TEXT[subscriptionType]}</div>}
                        </div>
                        {plan.prices[subscriptionType].oldPrice &&
                          <div className={styles.oldPrice}>${plan?.prices[subscriptionType].oldPrice}</div>}
                      </div>
                      {plan.items.map((item, index) => {
                        return (
                          <Flex key={index} className={styles.contentItem}>
                            <div className={styles.check}>
                              <MaterialIcon style={{ fontSize: 20 }} name='check' variant='check' />
                            </div>
                            <div className={styles.textWrapper}>
                              <div className={styles.text}>{item.title}</div>
                            </div>
                          </Flex>
                        )
                      })}
                    </Flex>
                  </Card>
                </div>
              </Col>
            )
          })
        }
      </Row>
    </Flex>
  )
}

export default Pricing
